import { DateTime } from 'luxon';
import { Duration } from 'luxon';
import {
  Period,
  TeamAnalyticsDimensionSubType,
  TeamAnalyticsResultItem
} from 'graphql/types';
import { getObject } from 'modules/notes/common/components/Timer';
import { utilizationColorSet } from '../UtilizationChart/utils';

export enum DropdownLabel {
  'hours' = 'hours',
  'utilization' = 'utilization',
  'billable' = 'billable'
}

export type categoriesDataType = {
  categoryName: string;
  color: string;
  data: { date: string; totalCount: number; totalDuration: number }[];
};
const defaultColor = '#607d8b';
export function getDaterange(currentPeriod: Period | null): Set<string> {
  const dateSet = new Set<string>();

  let startDate = DateTime.fromISO(currentPeriod?.startDate);
  const endDate = DateTime.fromISO(currentPeriod?.endDate);

  while (startDate <= endDate) {
    dateSet.add(startDate.toISODate());
    startDate = startDate.plus({ days: 1 });
  }

  return dateSet;
}

export function defaultTableCellData(dateSet: Set<string>) {
  const data = [...dateSet].reduce((acc, item) => {
    acc[item] = {
      scheduleHours: 0,
      actualHours: 0
    };
    return acc;
  }, {} as Record<string, Record<string, number>>);
  return {
    name: '',
    total: 0,
    data: data
  };
}

export function defaultTableCellDataBillable(dateSet: Set<string>) {
  const data = [...dateSet].reduce((acc, item) => {
    acc[item] = {
      scheduleHours: 0,
      billableHours: 0
    };
    return acc;
  }, {} as Record<string, Record<string, number>>);
  return {
    name: '',
    total: 0,
    data: data
  };
}

export function defaultTableCellDataUtilization(dateSet: Set<string>) {
  const data = [...dateSet].reduce((acc, item) => {
    acc[item] = {
      scheduleHours: 0,
      actualHours: 0,
      categoriesData: [] as categoriesDataType[]
    };
    return acc;
  }, {} as Record<string, { scheduleHours: number; actualHours: number; categoriesData: categoriesDataType[] }>);
  return {
    name: '',
    total: 0,
    data: data
  };
}

export type DropdownOptionType = {
  id: DropdownLabel;
  name: string;
};

export function transformAnalyticsDataSupervisor(
  data: (TeamAnalyticsResultItem | undefined | null)[] | undefined | null,
  currentPeriod: Period | null
) {
  if (!data) {
    return [];
  } else {
    const dateSet = getDaterange(currentPeriod);
    const dataSet = data.map((_data) => {
      if (_data) {
        const tableCell = defaultTableCellData(dateSet);

        let dateTotal = 0;
        tableCell.name = _data.user.displayText;
        _data.items?.forEach((i) => {
          i?.items?.forEach((j) => {
            if (
              j.dimension.dimensionType ===
              TeamAnalyticsDimensionSubType.MetricsByWorkSchedule
            ) {
              tableCell.data[i.date].scheduleHours = j.metrics.totalDuration;
            }
            if (
              j.dimension.dimensionType ===
              TeamAnalyticsDimensionSubType.MetricsByTimeEntryRevisionGroup
            ) {
              tableCell.data[i.date].actualHours = j.metrics.totalDuration;
              dateTotal += j.metrics.totalDuration;
            }
          });
        });
        tableCell.total = dateTotal;
        return tableCell;
      }
    });
    return dataSet;
  }
}

export function transformAnalyticsToBillable(
  data: (TeamAnalyticsResultItem | undefined | null)[] | undefined | null,
  currentPeriod: Period | null
) {
  if (!data) {
    return [];
  } else {
    const dateSet = getDaterange(currentPeriod);
    const dataSet = data.map((_data) => {
      if (_data) {
        const tableCell = defaultTableCellDataBillable(dateSet);

        let dateTotal = 0;
        tableCell.name = _data.user.displayText;
        _data.items?.forEach((i) => {
          i?.items?.forEach((j) => {
            if (
              j.dimension.dimensionType ===
              TeamAnalyticsDimensionSubType.MetricsByWorkSchedule
            ) {
              tableCell.data[i.date].scheduleHours = j.metrics.totalDuration;
            }
            if (
              j.dimension.dimensionType ===
              TeamAnalyticsDimensionSubType.MetricsByBillableDuration
            ) {
              tableCell.data[i.date].billableHours = j.metrics.totalDuration;
              dateTotal += j.metrics.totalDuration;
            }
          });
        });
        tableCell.total = dateTotal;
        return tableCell;
      }
    });
    return dataSet;
  }
}

export function transformAnalyticsToUtilization(
  data: (TeamAnalyticsResultItem | undefined | null)[] | undefined | null,
  currentPeriod: Period | null
) {
  if (!data) {
    return { data: [], categoriesLabels: {} };
  } else {
    const dateSet = getDaterange(currentPeriod);
    const categoriesLabels: Record<string, string> = {};
    const dataSet = data.map((_data) => {
      if (_data) {
        const tableCell = defaultTableCellDataUtilization(dateSet);
        let dateTotal = 0;
        tableCell.name = _data.user.displayText;
        _data.items?.forEach((i) => {
          let workHours = 0;
          i?.items?.forEach((j) => {
            if (
              j.dimension.dimensionType ===
              TeamAnalyticsDimensionSubType.MetricsByWorkSchedule
            ) {
              tableCell.data[i.date].scheduleHours = j.metrics.totalDuration;
            }
            if (
              j.dimension.dimensionType ===
              TeamAnalyticsDimensionSubType.MetricsByNoteCategory
            ) {
              dateTotal += j.metrics.totalDuration;
              workHours += j.metrics.totalDuration;
              tableCell.data[i.date].categoriesData.push({
                categoryName: j.dimension.name,
                color:
                  utilizationColorSet.get(j.dimension.name) || defaultColor,
                data: [
                  {
                    date: i.date,
                    totalDuration: j.metrics.totalDuration,
                    totalCount: j.metrics.totalCount
                  }
                ]
              });
              if (!categoriesLabels[j.dimension.name]) {
                categoriesLabels[j.dimension.name] =
                  utilizationColorSet.get(j.dimension.name) || defaultColor;
              }
            }
          });
          tableCell.data[i?.date].actualHours = workHours;
        });
        tableCell.total = dateTotal;
        return tableCell;
      }
    });
    return { data: dataSet, categoriesLabels: categoriesLabels };
  }
}

export const roundedDuration = (duration: number) => {
  const { seconds } = getObject(duration);
  if (seconds > 0)
    return seconds > 30
      ? duration
      : Duration.fromObject({ seconds: duration }).minus({ seconds: seconds })
          .seconds;
  else {
    return seconds < -30
      ? Duration.fromObject({ seconds: duration }).plus({ seconds: 60 }).seconds
      : duration;
  }
};
